import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FileSearch, Loader2 } from "lucide-react";

import { fetchFileBlob } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Renders a persisted upload in its source form. Auth is header-based, so the
 * file is fetched as a Blob via ``fetchFileBlob`` and shown through an object
 * URL (a bare ``<iframe src>`` to ``/api/files`` wouldn't carry the token).
 * PDFs render in an iframe (the browser's PDF viewer); scans/images in an
 * ``<img>``. The query only runs once the dialog mounts this component.
 */
function OriginalDocViewer({
  docId,
  fileName,
}: {
  docId: string;
  fileName?: string | null;
}) {
  const { data: blob, isPending, isError, error } = useQuery({
    queryKey: ["original-doc", docId],
    queryFn: () => fetchFileBlob(docId),
    staleTime: Infinity,
    retry: false,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (isPending || (!url && !isError)) {
    return (
      <div className="flex h-[70vh] items-center justify-center gap-2 text-sm text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Učitavam original…
      </div>
    );
  }

  if (isError || !url || !blob) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center gap-2 px-6 text-center text-sm text-red-700">
        <AlertTriangle className="h-5 w-5" />
        <span>
          Izvorni dokument nije moguće dohvatiti.
          {error instanceof Error ? ` (${error.message})` : ""}
        </span>
      </div>
    );
  }

  if (blob.type.startsWith("image/")) {
    return (
      <img
        src={url}
        alt={fileName ?? "izvorni dokument"}
        className="mx-auto max-h-[78vh] w-auto rounded-md border border-stone-200 object-contain"
      />
    );
  }

  return (
    <iframe
      title={fileName ?? "izvorni dokument"}
      src={url}
      className="h-[78vh] w-full rounded-md border border-stone-200 bg-white"
    />
  );
}

/**
 * "Pregledaj original" trigger → a large dialog showing the uploaded document
 * in its source form, next to (well, on top of) the extracted fields. Reused
 * by the opći-dio document cards and the situacija panel.
 */
export function OriginalDocButton({
  docId,
  fileName,
}: {
  docId: string;
  fileName?: string | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          title="Pregledaj izvorni dokument"
        >
          <FileSearch className="h-3.5 w-3.5" />
          <span className="ml-1 hidden sm:inline">Original</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-8">
            {fileName ?? "Izvorni dokument"}
          </DialogTitle>
        </DialogHeader>
        {/* Lazy: only fetch the blob once the dialog is actually open. */}
        {open && <OriginalDocViewer docId={docId} fileName={fileName} />}
      </DialogContent>
    </Dialog>
  );
}
